import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Search, ShoppingBag, LogIn, ChevronDown, Star, Download } from 'lucide-react';
import Header from '../../components/ui/Header';
import MenuGrid from './components/MenuGrid';
import Icon from '../../components/AppIcon';
import api, { resolveImageUrl } from '../../lib/api';
import { useLocation2 } from '../../contexts/LocationContext';
import { useCustomer } from '../../contexts/CustomerContext';

const ease = [0.16, 1, 0.3, 1];

const CATEGORIES = [
  { id: 'all', name: 'All' },
  { id: 'breakfast', name: 'Breakfast' },
  { id: 'specials', name: 'Specials' },
  { id: 'sunday-roast', name: 'Sunday Roast' },
  { id: 'soup', name: 'Soup' },
  { id: 'burgers', name: 'Burgers' },
  { id: 'sandwiches', name: 'Sandwiches' },
  { id: 'jacket-potatoes', name: 'Jacket Potatoes' },
  { id: 'hot-beverages', name: 'Hot Beverages' },
  { id: 'cold-beverages', name: 'Cold Beverages' },
];

const MenuCatalog = () => {
  const navigate = useNavigate();
  const { selectedLocation, setSelectedLocation, locations } = useLocation2();
  const { customer, logout: customerLogout } = useCustomer();
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('recommended');
  const [loading, setLoading] = useState(true);
  const [cartItems, setCartItems] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [siteStatus, setSiteStatus] = useState(null);

  const fetchMenuItems = async (locationSlug) => {
    setLoading(true);
    setFetchError(null);
    try {
      const data = await api.getMenuItems(locationSlug);
      const mapped = (data || []).map(item => ({
        id: item?.id,
        name: item?.name,
        subtitle: item?.subtitle,
        description: item?.description,
        price: parseFloat(item?.price),
        originalPrice: item?.original_price ? parseFloat(item?.original_price) : null,
        image: item?.show_image !== false ? resolveImageUrl(item?.thumbnail_url || item?.image_url) : null,
        imageAlt: item?.image_alt,
        category: item?.category,
        categories: item?.categories || [item?.category],
        dietary: item?.dietary || [],
        tags: item?.tags || [],
        featured: item?.featured,
        rating: parseFloat(item?.rating),
        reviewCount: item?.review_count,
        priceMatrix: item?.price_matrix || null,
        createdAt: item?.created_at,
      }));
      setMenuItems(mapped);
    } catch (err) {
      setFetchError(err?.message);
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenuItems(selectedLocation?.id);
    setActiveCategory('all');
    if (selectedLocation?.id) {
      api.getSiteStatus(selectedLocation.id).then(setSiteStatus).catch(() => setSiteStatus(null));
    }
  }, [selectedLocation]);

  useEffect(() => {
    const saved = localStorage.getItem('cartItems');
    if (saved) { try { setCartItems(JSON.parse(saved)); } catch {} }
  }, []);

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleLocationChange = (e) => {
    const loc = locations?.find(l => l?.id === e?.target?.value);
    if (loc) setSelectedLocation(loc);
  };

  const handleAddToCart = async (item) => {
    setCartItems(prev => {
      const existing = prev.find(c => c.id === item.id);
      let updated;
      if (existing) {
        updated = prev.map(c => c.id === item.id ? { ...c, quantity: c.quantity + 1 } : c);
      } else {
        updated = [...prev, { id: item.id, name: item.name, price: item.price, image: item.image, quantity: 1 }];
      }
      localStorage.setItem('cartItems', JSON.stringify(updated));
      return updated;
    });
    return new Promise((resolve) => setTimeout(resolve, 300));
  };

  // Category display order
  const CATEGORY_ORDER = { breakfast: 0, specials: 1, 'sunday-roast': 2, soup: 3, burgers: 4, sandwiches: 5, 'jacket-potatoes': 6, 'hot-beverages': 7, 'cold-beverages': 8 };

  // Filter + sort
  let filteredItems = [...menuItems];
  if (activeCategory !== 'all') {
    filteredItems = filteredItems.filter(item =>
      item.categories?.includes(activeCategory) || item.category === activeCategory
    );
  }
  if (sortBy === 'price-low') filteredItems.sort((a, b) => a.price - b.price);
  else if (sortBy === 'price-high') filteredItems.sort((a, b) => b.price - a.price);
  else if (sortBy === 'name') filteredItems.sort((a, b) => a.name.localeCompare(b.name));
  else {
    // 'recommended' — preserve the backend-configured sort_order (already returned in order).
    // Group by category for visual cohesion while keeping each group in its backend order.
    filteredItems.sort((a, b) => {
      const c = (CATEGORY_ORDER[a.category] ?? 99) - (CATEGORY_ORDER[b.category] ?? 99);
      if (c !== 0) return c;
      return (a.sort_order ?? 9999) - (b.sort_order ?? 9999);
    });
  }

  const categoryCounts = CATEGORIES.map(c => ({
    ...c,
    count: c.id === 'all'
      ? menuItems.length
      : menuItems.filter(i => i.categories?.includes(c.id) || i.category === c.id).length,
  }));

  // Items with a price matrix — shown separately as a shared banner and hidden from the grid.
  const CATEGORY_LABEL = { breakfast: 'Breakfast', specials: 'Specials', 'sunday-roast': 'Sunday Roast', soup: 'Soup', burgers: 'Burgers', sandwiches: 'Sandwiches', 'jacket-potatoes': 'Jacket Potatoes', 'hot-beverages': 'Hot Beverages', 'cold-beverages': 'Cold Beverages' };
  const matrixItems = filteredItems.filter(i => i.priceMatrix && Array.isArray(i.priceMatrix.rows) && i.priceMatrix.rows.length > 0);
  const displayItems = filteredItems.filter(i => !matrixItems.includes(i));
  // Group matrix items by category so each category shows its own banner
  const matricesByCategory = matrixItems.reduce((acc, it) => {
    const cat = it.category || 'other';
    (acc[cat] = acc[cat] || []).push(it);
    return acc;
  }, {});
  const orderedMatrixCategories = Object.keys(matricesByCategory).sort(
    (a, b) => (CATEGORY_ORDER[a] ?? 99) - (CATEGORY_ORDER[b] ?? 99)
  );

  return (
    <div className="min-h-screen" style={{ background: '#1A1410', color: '#F5EDE0' }}>
      <Header
        cartCount={cartCount}
        variant="dark"
        onCartClick={() => navigate('/shopping-cart')}
        onAccountClick={(a) => { if (a === 'login' || a === 'register') navigate('/customer-auth'); }}
        onSearch={() => {}}
        onLogout={() => {}}
      />

      <main className="pt-16">
        {/* Hero */}
        <section
          className="pt-14 pb-8 md:pt-20 md:pb-10 px-6 md:px-12 text-center relative overflow-hidden"
          style={{
            background:
              'radial-gradient(900px 500px at 50% 0%, rgba(85,186,63,.10) 0%, rgba(122,84,45,.10) 35%, #1A1410 70%)',
          }}
        >
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="text-xs tracking-[0.22em] uppercase mb-3"
            style={{ color: '#6FE04F', fontFamily: 'Outfit, sans-serif', fontWeight: 600 }}
          >
            {selectedLocation?.name || 'Our Menu'}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease }}
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4"
            style={{
              color: '#F5EDE0',
              fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
              fontWeight: 600,
              fontStyle: 'italic',
              letterSpacing: '-0.01em',
            }}
          >
            Our menu<span style={{ color: '#6FE04F', fontStyle: 'normal', fontWeight: 500 }}>.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease }}
            className="text-base md:text-lg max-w-xl mx-auto mb-6"
            style={{ color: '#C8BFB1', fontFamily: 'Outfit, sans-serif' }}
          >
            Fresh ingredients, family recipes, and honest cooking — all in one place.
          </motion.p>

          {/* Location + Status row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
          >
            {/* Location selector */}
            <div className="relative inline-flex items-center">
              <MapPin size={15} className="absolute left-4" style={{ color: '#6FE04F' }} />
              <select
                data-testid="location-selector"
                value={selectedLocation?.id || ''}
                onChange={handleLocationChange}
                className="appearance-none pl-9 pr-9 py-2.5 rounded-full text-sm font-medium cursor-pointer outline-none transition-colors"
                style={{
                  background: 'rgba(255,236,206,.06)',
                  color: '#F5EDE0',
                  border: '1px solid rgba(255,236,206,.14)',
                  fontFamily: 'Outfit, sans-serif',
                }}
              >
                {locations?.map(loc => (
                  <option key={loc?.id} value={loc?.id} style={{ background: '#221A12', color: '#F5EDE0' }}>{loc?.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 pointer-events-none" style={{ color: '#C8BFB1' }} />
            </div>

            {/* Site status pill */}
            {siteStatus && (
              <div
                data-testid="site-status-banner"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium"
                style={{
                  background: siteStatus.is_open ? 'rgba(85,186,63,.16)' : 'rgba(255,59,48,.16)',
                  color: siteStatus.is_open ? '#6FE04F' : '#FF6B6B',
                  border: `1px solid ${siteStatus.is_open ? 'rgba(85,186,63,.4)' : 'rgba(255,59,48,.4)'}`,
                  fontFamily: 'Outfit, sans-serif',
                }}
              >
                <span className={`w-2 h-2 rounded-full ${siteStatus.is_open ? 'bg-green-400 animate-pulse' : 'bg-red-500'}`} />
                {siteStatus.is_open ? 'Online Ordering Open' : 'Online Ordering Closed'}
              </div>
            )}

            {/* Download printed menu */}
            <a
              data-testid="download-menu-link"
              href="/la-cafe-menu.jpg"
              download="La-Cafe-Bistro-Menu.jpg"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium transition-colors"
              style={{
                background: 'rgba(111,224,79,.10)',
                color: '#6FE04F',
                border: '1px solid rgba(111,224,79,.36)',
                fontFamily: 'Outfit, sans-serif',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(111,224,79,.20)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(111,224,79,.10)'}
            >
              <Download size={14} />
              Download menu
            </a>

            {/* Quick actions — hidden when online ordering is closed */}
            {siteStatus?.is_open !== false && (
              <div className="flex items-center gap-2">
                <button
                  data-testid="track-order-link"
                  onClick={() => navigate('/order-status')}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium transition-colors"
                  style={{
                    background: 'rgba(255,236,206,.06)',
                    color: '#F5EDE0',
                    border: '1px solid rgba(255,236,206,.14)',
                    fontFamily: 'Outfit, sans-serif',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,236,206,.14)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,236,206,.06)'}
                >
                  <Search size={14} />
                  Track Order
                </button>
                {customer ? (
                  <span className="text-sm" style={{ color: '#C8BFB1' }}>
                    Hi, {customer.name?.split(' ')[0]}
                    <button
                      onClick={customerLogout}
                      className="ml-1.5 underline transition-colors"
                      style={{ color: '#F5EDE0' }}
                    >
                      Logout
                    </button>
                  </span>
                ) : (
                  <button
                    data-testid="login-to-order-btn"
                    onClick={() => navigate('/customer-auth')}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors"
                    style={{
                      background: '#55BA3F',
                      color: '#000',
                      fontFamily: 'Outfit, sans-serif',
                      boxShadow: '0 6px 18px rgba(85,186,63,.4)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#6FE04F'}
                    onMouseLeave={e => e.currentTarget.style.background = '#55BA3F'}
                  >
                    <LogIn size={14} />
                    Login to Order
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </section>

        {/* Category Tabs */}
        <section
          className="sticky top-16 z-30 py-4 px-6 md:px-12 backdrop-blur-xl"
          style={{ background: 'rgba(26,20,16,.85)', borderBottom: '1px solid rgba(255,236,206,.06)' }}
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Category pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 no-scrollbar w-full sm:w-auto">
              {categoryCounts.map(cat => {
                const isActive = activeCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    data-testid={`category-${cat.id}`}
                    onClick={() => setActiveCategory(cat.id)}
                    className="whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 shrink-0 la-pill-hover"
                    style={{
                      background: isActive ? '#55BA3F' : 'rgba(255,236,206,.04)',
                      color: isActive ? '#000' : '#C8BFB1',
                      border: `1px solid ${isActive ? '#55BA3F' : 'rgba(255,236,206,.10)'}`,
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: isActive ? 600 : 500,
                      boxShadow: isActive ? '0 4px 14px rgba(85,186,63,.4)' : 'none',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      if (!isActive) {
                        e.currentTarget.style.background = 'rgba(85,186,63,.10)';
                        e.currentTarget.style.borderColor = 'rgba(111,224,79,.35)';
                        e.currentTarget.style.color = '#F5EDE0';
                        e.currentTarget.style.boxShadow = '0 4px 14px rgba(85,186,63,.18)';
                      } else {
                        e.currentTarget.style.boxShadow = '0 6px 18px rgba(85,186,63,.55)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = '';
                      if (!isActive) {
                        e.currentTarget.style.background = 'rgba(255,236,206,.04)';
                        e.currentTarget.style.borderColor = 'rgba(255,236,206,.10)';
                        e.currentTarget.style.color = '#C8BFB1';
                        e.currentTarget.style.boxShadow = 'none';
                      } else {
                        e.currentTarget.style.boxShadow = '0 4px 14px rgba(85,186,63,.4)';
                      }
                    }}
                  >
                    {cat.name}
                    {cat.count > 0 && (
                      <span className="ml-1.5" style={{ opacity: isActive ? .7 : .5 }}>{cat.count}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Sort */}
            <div className="relative shrink-0">
              <select
                data-testid="sort-selector"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none pl-4 pr-8 py-2 rounded-full text-sm font-medium cursor-pointer outline-none"
                style={{
                  background: 'rgba(255,236,206,.06)',
                  color: '#F5EDE0',
                  border: '1px solid rgba(255,236,206,.14)',
                  fontFamily: 'Outfit, sans-serif',
                }}
              >
                <option value="recommended" style={{ background: '#221A12' }}>Recommended</option>
                <option value="name" style={{ background: '#221A12' }}>A — Z</option>
                <option value="price-low" style={{ background: '#221A12' }}>Price: Low</option>
                <option value="price-high" style={{ background: '#221A12' }}>Price: High</option>
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#C8BFB1' }} />
            </div>
          </div>
        </section>

        {/* Today's Picks rail */}
        {(() => {
          const picks = menuItems.filter(i => i?.featured);
          if (picks.length === 0) return null;
          const orderingOpen = siteStatus?.is_open !== false;
          return (
            <section className="px-6 md:px-12 pt-6 md:pt-8">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center justify-center w-7 h-7 rounded-full"
                      style={{ background: 'rgba(85,186,63,.18)', color: '#6FE04F' }}
                      aria-hidden="true"
                    >
                      <Star size={14} fill="currentColor" />
                    </span>
                    <h2
                      className="text-xl sm:text-2xl tracking-tight"
                      style={{
                        color: '#F5EDE0',
                        fontFamily: '"Cormorant Garamond", Georgia, serif',
                        fontStyle: 'italic',
                        fontWeight: 600,
                      }}
                    >
                      Today&apos;s picks
                    </h2>
                  </div>
                  <span className="text-xs tracking-[.18em] uppercase" style={{ color: '#6FE04F' }}>
                    Chef-selected
                  </span>
                </div>
                <div
                  data-testid="todays-picks-rail"
                  className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-6 md:-mx-12 px-6 md:px-12 snap-x snap-mandatory"
                >
                  {picks.map((item) => (
                    <button
                      key={item.id}
                      data-testid={`pick-${item.id}`}
                      onClick={() => orderingOpen && handleAddToCart(item)}
                      title={orderingOpen ? 'Add to cart' : 'View only — please order in store'}
                      className="snap-start shrink-0 w-72 sm:w-80 text-left rounded-2xl overflow-hidden transition-transform duration-200 hover:-translate-y-1 group"
                      style={{
                        background: '#221A12',
                        border: '1px solid rgba(255,236,206,.10)',
                        boxShadow: '0 6px 22px rgba(0,0,0,.35)',
                      }}
                    >
                      {/* Image intentionally hidden - row layout */}
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <h3
                            className="text-base font-medium leading-snug"
                            style={{ color: '#F5EDE0', fontFamily: 'Outfit, sans-serif' }}
                          >
                            {item.name}
                          </h3>
                          <span
                            className="text-base font-semibold tracking-tight shrink-0"
                            style={{ color: '#6FE04F', fontFamily: 'Outfit, sans-serif' }}
                          >
                            {item.priceMatrix ? `From £${item.price.toFixed(2)}` : `£${item.price.toFixed(2)}`}
                          </span>
                        </div>
                        {orderingOpen && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-[.14em] uppercase mt-2"
                            style={{ background: 'rgba(85,186,63,.18)', color: '#6FE04F' }}
                          >
                            <Star size={10} fill="currentColor" /> Pick
                          </span>
                        )}
                        {item.description && (
                          <p className="text-sm mt-2 line-clamp-2" style={{ color: '#C8BFB1' }}>
                            {item.description}
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </section>
          );
        })()}

        {/* Error */}
        {fetchError && (
          <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8">
            <div className="p-4 rounded-2xl text-sm text-center" style={{ background: 'rgba(255,107,107,.1)', color: '#FF6B6B', border: '1px solid rgba(255,107,107,.3)' }}>
              Failed to load menu. Please try again.
            </div>
          </div>
        )}

        {/* Shared Pricing Tables */}
        {orderedMatrixCategories.length > 0 && !loading && (
          <section className="px-6 md:px-12 pt-6 md:pt-8" data-testid="shared-pricing-tables">
            <div className="max-w-7xl mx-auto space-y-4">
              {orderedMatrixCategories.map(cat => (
                <div key={cat}>
                  <h3
                    className="text-lg sm:text-xl tracking-tight mb-2"
                    style={{
                      color: '#F5EDE0',
                      fontFamily: '"Cormorant Garamond", Georgia, serif',
                      fontStyle: 'italic',
                      fontWeight: 600,
                    }}
                  >
                    {CATEGORY_LABEL[cat] || cat} — Pricing
                  </h3>
                  {matricesByCategory[cat].map(mi => (
                    <div
                      key={mi.id}
                      data-testid={`shared-matrix-${mi.id}`}
                      className="mb-3 rounded-xl overflow-hidden"
                      style={{ background: 'rgba(255,236,206,.03)', border: '1px solid rgba(255,236,206,.10)' }}
                    >
                      {mi.priceMatrix.title && (
                        <div
                          className="px-4 py-2.5 text-xs font-semibold tracking-[.14em] uppercase"
                          style={{
                            color: '#6FE04F',
                            background: 'rgba(85,186,63,.08)',
                            fontFamily: 'Outfit, sans-serif',
                            borderBottom: '1px solid rgba(255,236,206,.10)',
                          }}
                        >
                          {mi.priceMatrix.title}
                        </div>
                      )}
                      {mi.description && (
                        <div className="px-4 py-2 text-sm" style={{ color: '#C8BFB1', fontFamily: 'Outfit, sans-serif' }}>
                          {mi.description}
                        </div>
                      )}
                      <table className="w-full text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <thead>
                          <tr>
                            {(mi.priceMatrix.columns || []).map((col, i) => (
                              <th
                                key={i}
                                className={`px-4 py-2 font-semibold ${i === 0 ? 'text-left' : 'text-right'}`}
                                style={{ color: '#F5EDE0', borderBottom: '1px solid rgba(255,236,206,.10)' }}
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {mi.priceMatrix.rows.map((row, ri) => (
                            <tr key={ri} style={{ borderTop: ri === 0 ? 'none' : '1px solid rgba(255,236,206,.06)' }}>
                              {row.map((cell, ci) => (
                                <td
                                  key={ci}
                                  className={`px-4 py-2 ${ci === 0 ? 'text-left font-medium' : 'text-right tabular-nums'}`}
                                  style={{ color: ci === 0 ? '#F5EDE0' : '#6FE04F' }}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Menu Grid */}
        <section className="py-6 md:py-8 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <MenuGrid
              items={displayItems}
              loading={loading}
              onAddToCart={handleAddToCart}
              isOrderingOpen={siteStatus?.is_open !== false}
            />
          </div>
        </section>

        {/* Floating Cart */}
        {cartCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
          >
            <button
              data-testid="floating-cart-btn"
              onClick={() => navigate('/shopping-cart')}
              className="inline-flex items-center gap-3 pl-6 pr-5 py-3.5 rounded-full shadow-2xl transition-transform hover:scale-105"
              style={{
                background: '#55BA3F',
                color: '#000',
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 600,
                boxShadow: '0 10px 32px rgba(85,186,63,.45)',
              }}
            >
              <ShoppingBag size={18} />
              <span className="text-sm">View Cart</span>
              <span
                className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
                style={{ background: 'rgba(0,0,0,.18)' }}
              >
                {cartCount}
              </span>
            </button>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default MenuCatalog;
